import React, { useState } from 'react';
import { Box } from '@mui/material';
import { Link } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import { acme } from '../../theme/acmeTheme';
import { mergeSx } from './utils/mergeSx';
import Button from './Button';
import Sheet from './Sheet';
import BrandLogoMark from './BrandLogoMark';

const navLinkSx = {
  fontSize: '0.875rem',
  color: acme.colors.mutedForegroundSolid,
  textDecoration: 'none',
  fontWeight: 400,
  transition: 'color 0.15s',
  '&:hover': { color: acme.colors.foreground },
};

/**
 * Acme-style marketing navbar (Tanplates/acme-hero.js nav block).
 * Used on public/marketing pages via HomeLayout.
 */
export default function MarketingNav({
  links = [],
  pricingHref = '/pricing',
  loginHref = '/login',
  registerHref = '/register',
  authenticated = false,
  rightSlot = null,
  sx,
}) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = (
    <>
      {links.map((item) => (
        <Box
          key={item.text}
          component={Link}
          to={item.path}
          sx={navLinkSx}
          onClick={() => setMobileOpen(false)}
        >
          {item.text}
        </Box>
      ))}
      <Box component={Link} to={pricingHref} sx={navLinkSx} onClick={() => setMobileOpen(false)}>
        Pricing
      </Box>
    </>
  );

  return (
    <>
      <Box
        component="nav"
        sx={mergeSx(
          {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: acme.radius.md,
            backgroundColor: acme.colors.background,
            border: `1px solid ${acme.colors.border}`,
            boxShadow: acme.shadow.lg,
            py: 1,
            px: 2,
          },
          sx,
        )}
      >
        <BrandLogoMark />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, md: 3 },
            ml: 'auto',
          }}
        >
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 3 }}>
            {navLinks}
          </Box>

          {!authenticated && (
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
              <Button
                component={Link}
                to={loginHref}
                variant="ghost"
                sx={{ minHeight: 28, px: 1.5, fontSize: '0.875rem' }}
              >
                Sign in
              </Button>
              <Button
                component={Link}
                to={registerHref}
                variant="primary-pill"
                sx={{ minHeight: 28, fontSize: '0.875rem' }}
              >
                Get started
              </Button>
            </Box>
          )}
          {authenticated && rightSlot}
          <Button
            variant="ghost"
            size="icon"
            sx={{ display: { md: 'none' }, minWidth: 28, minHeight: 28, width: 28, height: 28 }}
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <MenuIcon sx={{ fontSize: 15 }} />
          </Button>
        </Box>
      </Box>

      <Sheet open={mobileOpen} onClose={() => setMobileOpen(false)} side="right" width={280}>
        <Sheet.Header title="Menu" onClose={() => setMobileOpen(false)} />
        <Sheet.Body>
          {links.map((item) => (
            <Box
              key={item.text}
              component={Link}
              to={item.path}
              sx={mergeSx(navLinkSx, { py: 1 })}
              onClick={() => setMobileOpen(false)}
            >
              {item.text}
            </Box>
          ))}
          <Box component={Link} to={pricingHref} sx={mergeSx(navLinkSx, { py: 1 })} onClick={() => setMobileOpen(false)}>
            Pricing
          </Box>
          {!authenticated && (
            <>
              <Button component={Link} to={loginHref} variant="ghost" sx={{ justifyContent: 'flex-start' }}>
                Sign in
              </Button>
              <Button component={Link} to={registerHref} variant="default" sx={{ borderRadius: acme.radius.full }}>
                Get started
              </Button>
            </>
          )}
          {authenticated && rightSlot}
        </Sheet.Body>
      </Sheet>
    </>
  );
}
